<template>
	<div class="container">
		<Splitter style="height: 100%" stateKey="freeze.recordView.verticalSplitterLayout">
			<SplitterPanel :minSize="30">
				<Toolbar class="p-p-2">
					<template #start>
						<Button class="p-shadow-4" v-tooltip="$t('Squeeze.Validation.Buttons.BackToList')" icon="mdi mdi-chevron-double-up" @click="goBackToRecordList" />
					</template>
					<template #end></template>
				</Toolbar>
				<hr class="horizontal-line">
				<div v-if="loadingRecord">
					<div class="p-col-12 p-p-4">
						<div class="loadingSpinner" >
							<i class="mdi mdi-spin mdi-loading" style="fontSize: 8rem"></i>
						</div>
					</div>
				</div>
				<div v-else class="p-col-12 scroll-content p-shadow-2" ref="leftSplitterPanel">
					<div class="p-fluid p-formgrid p-grid p-p-2">
						<div class="field p-col-6 p-mb-2">
							<div class="p-input-filled">
								<label for="searchMask">{{$t('Freeze.SearchMask.Hitlist')}}</label>
								<Dropdown
									id="searchMask"
									v-model="selectedMask"
									:options="searchMasks"
									optionLabel="desc"
									:placeholder="$t('Freeze.SearchMask.ShowAll')"
									:filter="true"
									:autoFilterFocus="true"
									:showClear="true"
									:loading="loadingHitList"
									@change="selectSearchMask($event)"
									@show="showDropdownOverlay"
								/>
							</div>
						</div>
						<div class="field p-col-6 p-pt-4">
							<div class="p-input-filled">
								<Button
									:label="$t('Freeze.Eas.HeaderFields.Metadata')"
									@click="showMetaData = true"
									class="metaDataButton"
								/>
							</div>
						</div>
					</div>
					<RecordFields :fields="record.recordFields" :searchMaskFields="searchFields" />
				</div>
			</SplitterPanel>
			<SplitterPanel>
				<div class="p-col-12 p-shadow-2" style="height: 100%; width: 100%">
					<RecordViewer
						:record="record"
						:selectedDocument="selectedDocument"
						@onChangeDocument="openDocument"
					></RecordViewer>
				</div>
			</SplitterPanel>
		</Splitter>
		<Dialog v-model:visible="showMetaData"
				:style="{width: '50rem'}"
				:header="$t('Freeze.Eas.HeaderFields.Metadata')"
				:modal="true"
				:dismissableMask="true"
				@hide="showMetaData = false"
				position="top"
		>
			<table class="recordTable" style="height: min-content">
				<template v-for="(value, name) in record.headerFields"  v-bind:key="name">
					<tr class="p-p-2">
						<td class="p-text-bold p-p-2">
							{{ $t(`Freeze.Eas.HeaderFields.${name}`) }}:
						</td>
						<td>
							{{value}}
						</td>
					</tr>
				</template>
			</table>

			<div class="p-fluid p-formgrid p-grid p-mt-2">
				<div class="p-offset-6"></div>
				<div class="p-field p-col-6">
					<Button
						:label="$t('Forms.Hints.CopyClipboard')"
						icon="mdi mdi-content-copy"
						type="button"
						class="p-button"
						@click="onCopyMetadataToClipboard"
					/>
				</div>
			</div>
		</Dialog>
	</div>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, PropType, ref} from 'vue';
import {DocumentSearchRequestDto, PaginationDto} from "@dex/squeeze-client-ts";
import {FreezeSearchMask, FreezeSearchMaskField} from "@/apis/freeze/Types";
import RecordFields from "@/apps/freeze/components/RecordFields.vue";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import {ToastManager} from "@/util/ToastManager";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import Dropdown from "primevue/dropdown";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {useRoute} from "vue-router";
import RecordViewer from "@/apps/freeze/components/RecordViewer.vue";
import Dialog from "primevue/dialog";
import router from "@/router";
import {EasClientKey, FreezeClientKey} from "@/apps/freeze/DI";
import Toolbar from "primevue/toolbar";

export default defineComponent({
	name: 'RecordView',
	components: {
		Toolbar,
		RecordFields,
		Splitter,
		SplitterPanel,
		Dropdown,
		RecordViewer,
		Dialog,
	},
	props: {
		storeId: String,
		recordId: String,
		searchMaskId: String,
		searchRequest: {
			type: Object as PropType<DocumentSearchRequestDto>,
			default: () => ({}),
		},
		pagination: {
			type: Object as PropType<PaginationDto>,
			default: () => ({}),
		},
		tableSortStart: Array,
	},

	setup(props) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** list of all records */
		const records = ref<any[]>([]);

		/** Is the table currently loading */
		const loadingHitList = ref(false);
		const loadingRecord = ref(false);

		/** List of all Document-Class-Fields */
		const searchFields = ref<FreezeSearchMaskField[]>([]);

		/** Current record */
		const record = ref<any>({});

		/** Api for Sqeeuze-Requests */
		const freezeApi = inject(FreezeClientKey)!
		const easApi = inject(EasClientKey)!

		const selectedDocument = ref<any>({});

		/** List of all Stores */
		const searchMasks = ref<any[]>([]);

		/** Should the Metadata be shown? */
		const showMetaData = ref(false);

		/** Currently selected Store */
		const selectedMask = ref<FreezeSearchMask>({
			name: '',
			desc: '',
			fields: [],
		});

		/** Downloads a records attachment and returns
		 * @param store
		 * @param recordId
		 */
		const getRecord = async (store: string, recordId: string) => {
			try {
				loadingRecord.value = true;
				const recordRet = await easApi.getRecord(store, recordId);
				record.value = recordRet;
				loadingRecord.value = false;
				record.value.attachmentsLoading = true;
				record.value.attachments = await easApi.getRecordAttachments(props.storeId!, props.recordId!);

				//record.value.attachments.forEach((attachment: any) => record.value.url = `${ClientManager.getInstance().freezeUrl}/eas/archives/${props.storeId}/record/${record.value.id}/attachment/${attachment.id}`)

				for (const attachment of record.value.attachments) {
					const documentBase64 = await easApi.getAttachment(props.storeId!, props.recordId!, attachment.id!);
					attachment.documentBase64 = documentBase64;
				}

				if (record.value.attachments && record.value.attachments[0]) {
					const firstAttachment = record.value.attachments[0];
					record.value.currentDocumentBase64 = firstAttachment.documentBase64;
					record.value.currentDocumentName = firstAttachment.name;

					selectedDocument.value = firstAttachment;
				}
			} catch (e) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + e);
			} finally {
				loadingRecord.value = false;
				record.value.attachmentsLoading = false;
			}
		}

		/**
		 * Get Search Mask
		 * @param name
		 */
		const getSearchMask = async (name: string) => {
			try {
				const data = await freezeApi.getSearchMask(name);
				if (data) {
					searchFields.value = data.fields;
				}
			} catch(e) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + e);
			}
		}

		/** Get all Search Masks */
		const getAllSearchMasks = async () => {
			try {
				loadingHitList.value = true;
				const data = await freezeApi.getAllSearchMasks();
				searchMasks.value = data;

				if (route.query.hitList) {
					const queryMask = searchMasks.value.find(mask => mask.name === route.query.hitList);
					if (queryMask) {
						selectedMask.value = queryMask;
						await getSearchMask(queryMask.name);
					} else {
						// first of all available hit lists is used as the hit list
						selectedMask.value = searchMasks.value[0];
						await getSearchMask(searchMasks.value[0].name);
					}
				} else {
					// first of all available hit lists is used as the hit list when query not exists
					selectedMask.value = searchMasks.value[0];
					await getSearchMask(searchMasks.value[0].name);
				}
			} catch(e) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + e);
			} finally {
				loadingHitList.value = false;
			}
		}

		onMounted(() => {
			getRecord(props.storeId!, props.recordId!);
			getAllSearchMasks();
		});

		/** Set the selected search mask */
		const selectSearchMask = (event: { value: FreezeSearchMask }) => {
			if (!event.value) {
				searchFields.value = [];
				router.replace({ path: route.path, query: { hitList: undefined }})
				return;
			}
			selectedMask.value = event.value;
			getSearchMask(event.value.name);

			router.replace({ path: route.path, query: { hitList: selectedMask.value.name }})
		}

		/**
		 * Opens a document in wider view
		 * @param currentDocument
		 */
		const openDocument = (currentDocument: any) => {
			selectedDocument.value = currentDocument;
			if (!selectedDocument.value.name.toLowerCase().includes(".pdf") &&
				!selectedDocument.value.name.toLowerCase().includes(".png") &&
				!selectedDocument.value.name.toLowerCase().includes(".jpg") &&
				!selectedDocument.value.name.toLowerCase().includes(".jpeg")
			) {
				return;
			}

			record.value.currentDocumentBase64 = selectedDocument.value.documentBase64;
			record.value.currentDocumentName = selectedDocument.value.name;
		}

		/** Change the View to the Overview */
		const goBackToRecordList = () => {
			router.replace({
				name: 'FreezeRecords',
				params: {
					storeId: "" + props.storeId,
					searchMaskId: props.searchMaskId ? "" + props.searchMaskId : searchMasks.value[0].name,
					searchRequest: JSON.stringify(props.searchRequest),
					tableSortStart: JSON.stringify(props.tableSortStart),
					pagination: JSON.stringify(props.pagination),
				},
			});
		}

		/** Triggered if user wants to copy the metadata to clipboard */
		const onCopyMetadataToClipboard = () => {
			const labels = record.value.headerFields;
			if (labels && navigator.clipboard) {
				const text = Object
					.keys(labels)
					.map(key => {
						const translatedKey = t(`Freeze.Eas.HeaderFields.${key}`);
						return `${translatedKey}: ${labels[key]}`;
					})
					.join("\n");

				navigator.clipboard.writeText(text)
					.then(() => {
						ToastManager.showSuccess(toast, t('Toasts.Title.Success'), t('Forms.Hints.CopyClipboardDone'));
					});
			}
		}

		return {
			records,
			searchFields,
			record,
			selectedDocument,
			searchMasks,
			selectedMask,
			loadingHitList,
			loadingRecord,
			showMetaData,
			selectSearchMask,
			openDocument,
			getRecord,
			showDropdownOverlay,
			goBackToRecordList,
			onCopyMetadataToClipboard,
		}
	},
});

</script>

<style lang="scss" scoped>

.container {
	display: flex;
	flex-flow: column nowrap;
	height: 100vh;
}

.header {
	height: 100px;
}

.content {
	height: calc(100vh - 100px - 15px);

	@media only screen and (max-width: 767px) {
		height: 100vh;
	}
}

.horizontal-line {
	width: 100%;
	border: 0.063rem solid var(--surface-300);
	margin: 0;
}

.scroll-content {
	flex: 1 1 0;
	align-self: auto;
	height: 100%;
	overflow-y: auto;
}

.loadingSpinner {
	background: white;
	text-align: center;
	vertical-align: middle;
	margin-top: 25%;
}

.base64Image {
	max-width: 100%;
	max-height: 100%;
	height: inherit !important;
}

button.p-button.p-component.metaDataButton {
	top: -0.125rem;
}

.recordTable {
	width: auto;
}

::v-deep(.p-splitter-gutter-handle) {
	background: var(--dex-primary-color) !important;
	width: 10px !important;
	cursor: pointer;
	transform: scale(1);
}

</style>

